/*
 * Browse and filter.
 *
 * The two filters the requirement asks for - product and category, one or
 * more of each - are multi-select checkbox lists. Within one axis the selections
 * are an OR ("any of these products"); across axes they are an AND. That is stated
 * in the panel rather than left for the reader to infer, because the opposite
 * reading is just as plausible and produces a very different result set. The
 * product list stays multi-select although an article carries exactly one
 * product: the list is what the READER is asking about, not what an article
 * holds, and "show me accoPLANNING and accoCOMMENT" is a browse worth keeping.
 *
 * Filter state lives entirely in the query string. That makes a filtered view
 * linkable and bookmarkable, survives a reload, and means the back button does what
 * it looks like it should - none of which is true of state held in a component.
 *
 * The PRODUCT box and the query-string readers are shared with the release-note
 * list since S42 (2026-10-12): ProductFilter.tsx and urlState.ts. Moved out with
 * their output unchanged; what stays here is where this page's options come from,
 * the bootstrap's article facet. The search card and its behaviour followed for
 * S43 (2026-10-13), when the release-note list gained the same search:
 * SearchFilter.tsx, the markup unchanged here too.
 *
 * Its words are the chosen language's since S48 (2026-10-29) - the counts, the
 * sort options, the empty states, the filter rule - from i18n/. The product and
 * category names in the panels are the administrators', and stay as written.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import {
  MAX_PROMOTED_ARTICLES,
  articleHref,
  splitSearchWords,
  type PublicArticleFilter,
  type PublicArticleProduct,
  type PublicArticleSummary,
} from '@accotool-kb/shared';
import { getPromotedArticles, listArticles } from '../api/publicApi.js';
import { forReader } from '../api/publicReader.js';
import { usePublicReader } from '../auth/AuthProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import {
  AudienceTags,
  CheckboxList,
  DateTime,
  EmptyState,
  ErrorBanner,
  Loading,
  Pagination,
  Tag,
} from '../components/ui.js';
import { usePublicContext } from './PublicLayout.js';
import { ProductFilter } from './ProductFilter.js';
import { SearchFilter, useUrlSearch } from './SearchFilter.js';
import { parseIds, parsePage } from './urlState.js';
import { SignedInAs } from '../site/SignedInAs.js';
import { articlesHeading, listPageTabTitle } from './pageHeadings.js';
import { useLanguage } from '../i18n/language.js';
import { en, type Messages } from '../i18n/en.js';
import { richText } from '../i18n/richText.js';

const PAGE_SIZE = 20;

/*
 * A narrowed view is grouped by product; plain browsing stays a flat list.
 * Asking a question - by search or by category - makes the product the next
 * thing that decides whether a hit is relevant, so the answer is laid out
 * product-first. Two functions below: which layout, and how to build it.
 */

/**
 * Whether the result list is laid out in product groups.
 *
 * The rule: group whenever the layout has more than one product to say
 * something about. A search or a category tag both leave the product axis
 * open, and that is exactly when a product-first layout earns its headings.
 *
 * Products are the one axis that has to count: with SEVERAL ticked the groups
 * separate the results per product, which is the whole point; with exactly ONE
 * ticked every result carries it - an article has one product and the server
 * filtered on it - so a single heading would only repeat the checkbox the reader
 * just used.
 *
 * Reads the RESPONSE's echoed filter, never the live input, and that choice
 * pays off twice here: the fetch trails the debounced search text (keying off
 * the input would regroup stale data for the whole in-flight window), and the
 * server has already deduplicated the id list, so `?products=1,1` counts as
 * the one product it is rather than as two.
 */
export function shouldGroupByProduct(filter: PublicArticleFilter): boolean {
  return (
    filter.search !== null ||
    filter.categoryIds.length > 0 ||
    filter.productIds.length > 1
  );
}

/** One product heading and the articles under it. */
export interface ProductGroup {
  product: PublicArticleProduct;
  items: PublicArticleSummary[];
}

/**
 * The groups themselves.
 *
 * This is a PARTITION, and that is the whole of the rule: an article carries
 * exactly one product - kb.Article.ProductId is NOT NULL and there is no
 * junction left to carry a second - so every article lands in one group, nothing
 * is duplicated across groups, and there is no untagged remainder to gather up.
 * Two rules that used to live here went with the states they described: an
 * article under several headings, and a trailing group for one under none. So
 * did the defensive de-duplication of the tag list - the product arrives as one
 * joined row rather than as a comma-joined aggregate, so a product name
 * containing a comma is now just a name containing a comma.
 *
 * Keyed on the product SLUG and labelled with its name. The slug is the identity
 * half - unique, CHECK-constrained, and the half that goes in a URL - while the
 * name is only what we print; keying on the name would fold two products
 * together for as long as a rename left them sharing one.
 *
 * There is deliberately no "only these products" parameter any more. It existed
 * because a multi-product article could open a heading for a product the reader
 * had not ticked; now the server filters on the article's one product, so every
 * row on the page already belongs to a ticked product and a second filter here
 * could only ever disagree with the server about the result set it was handed.
 *
 * Groups are alphabetical by name; within a group the server's sort order (the
 * sort dropdown) is preserved. Grouping happens within the fetched page - the
 * pagination contract with the server is unchanged.
 */
export function groupByProduct(items: PublicArticleSummary[]): ProductGroup[] {
  const bySlug = new Map<string, ProductGroup>();

  for (const item of items) {
    const group = bySlug.get(item.product.slug);
    if (group) group.items.push(item);
    else bySlug.set(item.product.slug, { product: item.product, items: [item] });
  }

  // The comparison is pinned to one locale: the product names are content,
  // which is single-language, and group order must depend neither on the
  // viewer's OS locale nor on the language chosen for the site's own words
  // (S48). numeric keeps "Product 2" ahead of "Product 10".
  return [...bySlug.values()].sort((a, b) =>
    a.product.name.localeCompare(b.product.name, 'en', { sensitivity: 'base', numeric: true }),
  );
}

/**
 * What the default sort is called: "Best match" only when the server will rank
 * this search, "Most recent" otherwise - so the label never claims an order the
 * database did not produce.
 *
 * The server ranks a search when the database has the article index ready
 * (`fullText`, the bootstrap's answer) AND the search has at least one PLAIN
 * word: only plain words reach the ranked CONTAINSTABLE join. Since 2026-10-13
 * the search goes word by word, and a word with punctuation inside - "v1.1",
 * "e-mail" - is matched as typed beside the join rather than through it, so a
 * search of such words alone is ordered by date even with the index. Until then
 * this was "a search, and fullText" - which also said "Best match" for a search
 * of blanks the server treats as none. The words are splitSearchWords', the
 * server's own rule, so the two cannot disagree about what a plain word is.
 *
 * In the chosen language's words since S48 (2026-10-29): `messages` is the
 * dictionary, English when none is passed.
 */
export function defaultSortLabel(search: string, fullText: boolean, messages: Messages = en): string {
  return fullText && splitSearchWords(search).some((word) => word.plain)
    ? messages.browse.sortBestMatch
    : messages.browse.sortMostRecent;
}

function ArticleCard({ article }: { article: PublicArticleSummary }): JSX.Element {
  return (
    <li className="article-card">
      <h3>
        {/* The card's whole address: the product segment is not decoration, it
            is half of what identifies the article being linked to. */}
        <Link to={articleHref(article.product.slug, article.slug)}>{article.title}</Link>
      </h3>

      <div className="article-meta">
        <DateTime value={article.publishAtUtc} dateOnly />
      </div>

      {article.excerpt && <p className="article-card-excerpt">{article.excerpt}</p>}

      <div className="tag-list">
        <Tag kind="product">{article.product.name}</Tag>
        {article.categoryTags.map((tag) => (
          <Tag key={`c-${tag}`} kind="category">
            {tag}
          </Tag>
        ))}
        {/* Who it is for, on a private article (S46) - the owner's tags. */}
        <AudienceTags article={article} />
      </div>
    </li>
  );
}

export function BrowsePage(): JSX.Element {
  const { bootstrap } = usePublicContext();
  const { language } = useLanguage();
  const t = language.messages;
  const [searchParams, setSearchParams] = useSearchParams();

  const products = parseIds(searchParams.get('products'));
  const categories = parseIds(searchParams.get('categories'));
  const page = parsePage(searchParams.get('page'));
  // A sort the dropdown does not offer falls back to 'recent' rather than being
  // passed through: the server answers an unknown sort with 400, and a bookmarked
  // URL can carry a value the dropdown no longer offers.
  const sortParam = searchParams.get('sort');
  const sort: 'recent' | 'title' = sortParam === 'title' ? sortParam : 'recent';

  const update = (changes: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (resetPage) next.delete('page');
    setSearchParams(next);
  };

  /*
   * The text box is local so typing is never throttled by the URL; the URL is
   * written per settled value rather than per keystroke, and the list is fetched
   * for what the URL says - so the two can never disagree. The rules, shared
   * with the release-note list since S43 (2026-10-13): useUrlSearch.
   */
  const { draft: searchDraft, setDraft: setSearchDraft, search } = useUrlSearch(
    searchParams.get('q') ?? '',
    (settled) => update({ q: settled || null }),
  );

  const filter = useMemo(
    () => ({
      products,
      categories,
      q: search,
      page,
      pageSize: PAGE_SIZE,
      sort,
    }),
    [products.join(','), categories.join(','), search, page, sort],
  );

  // As the reader (S46): a private article is listed for its audiences only.
  const reader = usePublicReader();
  const state = useAsync(
    (signal) => forReader(reader, () => listArticles(filter, signal, reader)),
    [filter, reader.ready, reader.key],
  );

  /*
   * The articles page's own title and the line under it (2026-10-15) - the site
   * title headed this page until then, and is now the header's and the tab's
   * alone - and the browser tab named after the page, as the release-note list
   * names its own. Set again once the first page of results has arrived, for
   * the reason ReleaseNotesPage gives (the branding provider stamps the bare
   * site title in the commit after this page mounts), and given back on the
   * way out. While the articles title is the site title - as it starts - the
   * tab reads the site title once.
   */
  const heading = articlesHeading(bootstrap.site, language.code);
  const loaded = state.data !== null;
  useEffect(() => {
    document.title = listPageTabTitle(heading.title, bootstrap.site.title);
    return () => {
      document.title = bootstrap.site.title;
    };
  }, [heading.title, bootstrap.site.title, loaded]);

  /*
   * The promoted tiles: fetched once - the shortlist does not change with the
   * filters - and shown only on the untouched first page. The moment a search,
   * a filter or a page number enters the picture, the reader has asked a
   * question, and tiles that ignore the question would just push the answer
   * below the fold.
   */
  const promoted = useAsync(
    (signal) => forReader(reader, () => getPromotedArticles(signal, reader)),
    [reader.ready, reader.key],
  );
  // The decision reads the RESPONSE's echoed filter for the same reason the
  // grouped view does: keyed off the input, the tiles would reappear above
  // still-grouped search results for the whole debounce+fetch window after
  // clearing a search.
  const echoedFilter = state.data?.filter;
  const showPromoted =
    echoedFilter !== undefined &&
    echoedFilter.search === null &&
    echoedFilter.page === 1 &&
    echoedFilter.productIds.length === 0 &&
    echoedFilter.categoryIds.length === 0;

  const groupedByProduct = echoedFilter !== undefined && shouldGroupByProduct(echoedFilter);

  const activeFilterCount = products.length + categories.length;
  const axesInUse = (products.length > 0 ? 1 : 0) + (categories.length > 0 ? 1 : 0);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{heading.title}</h1>
          {heading.subtitle && <p>{heading.subtitle}</p>}
        </div>
      </div>

      <div className="browse-layout">
        {/*
          Two headings nobody sees. The page's outline used to run from h1 (the
          site title) straight to h3 (the sidebar's "Product" and "Category",
          every card's title) whenever the list was flat - the only h2s were the
          group headings, which exist only while the results are grouped. A
          reader moving by heading level heard a skipped level and could not tell
          the filters from the list, both sitting at the same depth. So each
          region now opens with a visually hidden h2 that is also what names the
          landmark (aria-labelledby, rather than the same word twice), and the h3s
          below are nested properly in both the flat and the grouped view.
          W11's last-but-one carry-over, 2026-09-23.
        */}
        <aside className="sidebar" aria-labelledby="filters-heading">
          <h2 id="filters-heading" className="visually-hidden">
            {t.filters.heading}
          </h2>
          <SearchFilter label={t.browse.searchLabel} value={searchDraft} onChange={setSearchDraft} />

          {/*
            The combination rule, said once and in one place. It only earns its
            space once more than one list is in use, which is exactly when the
            reader would otherwise have to guess.
          */}
          {axesInUse > 1 && (
            <p className="faint" style={{ margin: 0 }}>
              {richText(t.filters.combinationRule)}
            </p>
          )}

          {bootstrap.products.length > 0 && (
            <ProductFilter
              selected={products}
              onChange={(next) => update({ products: next.join(',') || null })}
              items={bootstrap.products.map((product) => ({
                id: product.productId,
                label: product.name,
                ...(product.articleCount !== undefined ? { count: product.articleCount } : {}),
              }))}
            />
          )}

          {bootstrap.categories.length > 0 && (
            <div className="sidebar-group card card-tight">
              <h3>{t.filters.category}</h3>
              <p className="faint" style={{ margin: '0 0 0.5rem' }}>
                {t.filters.categoryRule}
              </p>
              <CheckboxList
                name="categories"
                selected={categories}
                onChange={(next) => update({ categories: next.join(',') || null })}
                items={bootstrap.categories.map((category) => ({
                  id: category.categoryId,
                  label: category.name,
                  ...(category.articleCount !== undefined ? { count: category.articleCount } : {}),
                }))}
              />
            </div>
          )}

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="button button-quiet"
              onClick={() => update({ products: null, categories: null })}
            >
              {t.filters.clear(activeFilterCount)}
            </button>
          )}

          <SignedInAs />
        </aside>

        <section aria-labelledby="articles-heading">
          <h2 id="articles-heading" className="visually-hidden">
            {t.browse.articles}
          </h2>
          {showPromoted && promoted.data && promoted.data.items.length > 0 && (
            <>
              <h2 className="article-group-heading">{t.browse.promoted}</h2>
              {/*
                One row, never wrapping: the stylesheet decides how many tiles
                fit at the current width and hides the rest, so the markup
                always carries the full shortlist and the CSS carries the
                responsive rule. Cards are the SAME component as the list below
                - the tile look (clamped excerpt, equal heights) is entirely
                .promoted-row's doing.
              */}
              {/*
                The wrapper is the container the tile stylesheet queries: how
                many tiles may show is a decision about the ARTICLE COLUMN's
                real width, not the viewport's - the sidebar and page padding
                sit between the two.
              */}
              <div className="promoted-viewport">
                <ul
                  className="promoted-row"
                  /*
                    The stylesheet needs the tile count to size the columns:
                    min(count, width cap) is what lets two promotions take
                    half the column each instead of two fifths and a gap.
                  */
                  style={{ '--promoted-count': promoted.data.items.length } as React.CSSProperties}
                >
                  {promoted.data.items.slice(0, MAX_PROMOTED_ARTICLES).map((article) => (
                    <ArticleCard key={article.articleId} article={article} />
                  ))}
                </ul>
              </div>
            </>
          )}

          <div className="toolbar">
            <p className="muted toolbar-grow" style={{ margin: 0 }} aria-live="polite">
              {state.data ? t.browse.count(state.data.total, groupedByProduct) : ' '}
            </p>
            <div className="field">
              <label htmlFor="sort" className="visually-hidden">
                {t.browse.sortBy}
              </label>
              <select
                id="sort"
                value={sort}
                onChange={(event) => update({ sort: event.target.value })}
              >
                {/* The default order is by relevance only for a search the
                    server can rank, and the label says which: defaultSortLabel. */}
                <option value="recent">{defaultSortLabel(search, bootstrap.search.fullText, t)}</option>
                <option value="title">{t.browse.sortTitle}</option>
              </select>
            </div>
          </div>

          {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}

          {state.loading && !state.data && <Loading label={t.browse.finding} />}

          {state.data && state.data.items.length === 0 && (
            <EmptyState title={t.browse.nothingMatches}>
              <p>{activeFilterCount > 0 || search ? t.browse.tryBroader : t.browse.noneYet}</p>
            </EmptyState>
          )}

          {state.data && state.data.items.length > 0 && (
            <>
              {/* The rule, and why it reads the echoed filter: shouldGroupByProduct. */}
              {groupedByProduct ? (
                <>
                  {/*
                    The page scope, and only that, said only when there is more
                    than one page of it: a heading that repeats on the next page
                    with different articles under it otherwise reads as a reset.
                    Nothing has to be said about an article appearing twice any
                    more - it carries one product, so the group sizes add up to
                    the article count the toolbar prints.
                  */}
                  {state.data.total > PAGE_SIZE && (
                    <p className="faint" style={{ margin: '0 0 var(--space-3)' }}>
                      {t.browse.groupsReflectPage}
                    </p>
                  )}
                  {groupByProduct(state.data.items).map((group) => (
                    <section key={group.product.slug} className="article-group">
                      <h2 className="article-group-heading">{group.product.name}</h2>
                      <ul className="article-list">
                        {group.items.map((article) => (
                          <ArticleCard key={article.articleId} article={article} />
                        ))}
                      </ul>
                    </section>
                  ))}
                </>
              ) : (
                <ul className="article-list">
                  {state.data.items.map((article) => (
                    <ArticleCard key={article.articleId} article={article} />
                  ))}
                </ul>
              )}

              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={state.data.total}
                onChange={(nextPage) => update({ page: String(nextPage) }, false)}
              />
            </>
          )}
        </section>
      </div>
    </>
  );
}
